import React, { useEffect, useState } from "react";
import {
    BeneficiaryResponseModel,
    BeneficiaryTypeEnum,
    InputDatePickerTimeDisplayModeEnum,
    RequestActionModel,
    SearchTransactionsParams,
    SelectItemModel,
} from "../../../../../models";
import { useFormik } from "formik";
import {
    formatToIsoString,
    getLabelName,
    isArabicCurrentLanguage,
} from "../../../../../utils";
import {
    RadioCheckBox,
    TextBox,
    ButtonBox,
    SelectBox,
    LoadingBox,
    ModelDialogBox,
    InputDatePicker,
    CheckBox,
} from "../../../..";
import { BeneficiaryButtonList } from "../../../../beneficiary/beneficiaryButtonList";
import { useStoredEmployees } from "../../../../../hooks";

interface TransactionOrdersFormProps {
    setSearchParams: React.Dispatch<React.SetStateAction<SearchTransactionsParams>>;
    beneficiary: BeneficiaryResponseModel;
    initialSearchParams: SearchTransactionsParams;
}

const TransactionOrdersForm: React.FC<TransactionOrdersFormProps> = ({
                                                                         setSearchParams,
                                                                         beneficiary,
                                                                         initialSearchParams,
                                                                     }) => {
    //#region formik
    const { values, setFieldValue, handleSubmit } = useFormik({
        initialValues: initialSearchParams,
        onSubmit: () => {
            setSearchParams(values);
        },
    });
    //#endregion

    //#region variables
    const { employeesLoading, storedEmployeeList } = useStoredEmployees();
    const [showSelectBeneficiary, setShowSelectBeneficiary] = useState(false);
    const isFormLoading = employeesLoading;
    const isArabic = isArabicCurrentLanguage();
    //#endregion

    //#region useEffect
    useEffect(() => {
        setFieldValue("customerId.value", beneficiary.value);
        setFieldValue(
            "customerId.label",
            isArabic ? beneficiary.nameAr : beneficiary.name
        );
    }, []);
    //#endregion

    //#region html
    return (
        <LoadingBox isLoading={isFormLoading}>
            <form onSubmit={handleSubmit}>
                {/* ROW 1 (Customer / From / To) */}
                <div className="row g-3 mb-2 align-items-center">
                    {/* Customer */}
                    <div className="col-12 col-xl-5">
                        <div className="d-flex gap-2 align-items-center">
                            <CheckBox
                                id="customer-active"
                                inputName="customer-active"
                                labelName={getLabelName("customer")}
                                checked={values.customerId.active}
                                onChange={(e: any) =>
                                    setFieldValue("customerId.active", e.target.checked)
                                }
                            />

                            <input
                                value={values.customerId.label}
                                style={{ width: "100%", height: "38px" }}
                                type="button"
                                placeholder={values.customerId.label}
                                onClick={() => setShowSelectBeneficiary(true)}
                                disabled={!values.customerId.active}
                            />
                        </div>
                    </div>

                    {/* From Date (controls both From/To enable state) */}
                    <div className="col-12 col-xl-3">
                        <div className="d-flex align-items-center gap-2 flex-nowrap">
                            <div className="flex-shrink-0">
                                <CheckBox
                                    id="fromdate-active"
                                    inputName="fromdate-active"
                                    labelName={getLabelName("from date")}
                                    checked={values.fromDate.active}
                                    onChange={(e: any) => {
                                        const checked = e.target.checked;
                                        setFieldValue("fromDate.active", checked);
                                        setFieldValue("toDate.active", checked); // keep state consistent
                                    }}
                                />
                            </div>

                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <InputDatePicker
                                    id="fromDatePicker"
                                    className="form-control"
                                    name="fromDate"
                                    timeMode={InputDatePickerTimeDisplayModeEnum.None}
                                    selectedDate={new Date(values.fromDate.value!.toString())}
                                    InputLabel=""
                                    disabled={!values.fromDate.active}
                                    onChange={(date: Date) => {
                                        const v = formatToIsoString(date);
                                        setFieldValue("fromDate", {
                                            value: v,
                                            active: values.fromDate.active,
                                        });
                                    }}
                                    maxDate={new Date()}
                                />
                            </div>
                        </div>
                    </div>

                    {/* To Date (enabled/disabled by same checkbox) */}
                    <div className="col-12 col-xl-3">
                        <div className="d-flex align-items-center gap-2 flex-nowrap">
                            <div className="flex-shrink-0">
                                <label className="m-0" style={{ whiteSpace: "nowrap" }}>
                                    {getLabelName("to date")}
                                </label>
                            </div>

                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <InputDatePicker
                                    id="toDatePicker"
                                    className="form-control"
                                    name="toDate"
                                    timeMode={InputDatePickerTimeDisplayModeEnum.None}
                                    selectedDate={new Date(values.toDate.value!.toString())}
                                    InputLabel=""
                                    disabled={!values.fromDate.active}   // ✅ same checkbox
                                    onChange={(date: Date) => {
                                        const v = formatToIsoString(date);
                                        setFieldValue("toDate", {
                                            value: v,
                                            active: values.toDate.active,
                                        });
                                    }}
                                    maxDate={new Date()}
                                />
                            </div>
                        </div>
                    </div>
                </div> {/* ✅ CLOSE ROW 1 */}

                {/* ROW 2 (Cashier / Radio / Search) */}
                <div className="row g-2 mb-3 align-items-center">
                    {/* Cashier */}
                    <div className="col-12 col-xl-5">
                        <div className="d-flex gap-2 align-items-center">
                            <CheckBox
                                layout="inline"
                                id="employeeId-active"
                                inputName="employeeId-active"
                                labelName={getLabelName("cashier")}
                                checked={values.employeeId.active}
                                onChange={(e: any) =>
                                    setFieldValue("employeeId.active", e.target.checked)
                                }
                            />

                            <div className="flex-grow-1">
                                <SelectBox
                                    labelName=""
                                    inputName="employeeId"
                                    isDisabled={!values.employeeId.active}
                                    isDataLoading={employeesLoading}
                                    selectedValues={
                                        values.employeeId.value === null
                                            ? null
                                            : ["" + values.employeeId.value]
                                    }
                                    source={storedEmployeeList}
                                    onStatusChange={(selectedValue: SelectItemModel) => {
                                        if (selectedValue === null) {
                                            setFieldValue("employeeId.value", null);
                                            setFieldValue("employeeId.label", "");
                                            return;
                                        }
                                        setFieldValue("employeeId.value", selectedValue.value);
                                        setFieldValue("employeeId.label", selectedValue.label);
                                    }}
                                    isSingleSelect={true}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Radio + Search */}
                    <div className="col-12 col-xl-7">
                        <div className="d-flex align-items-center gap-2 flex-nowrap">
                            <div style={{ whiteSpace: "nowrap" }}>
                                <RadioCheckBox
                                    items={[
                                        {
                                            text: getLabelName("Mobile"),
                                            value: "customerMobile",
                                            name: "searchBy.value",
                                        },
                                        {
                                            text: getLabelName("number"),
                                            value: "iD",
                                            name: "searchBy.value",
                                        },
                                        {
                                            text: getLabelName("table"),
                                            value: "tableId",
                                            name: "searchBy.value",
                                        },
                                    ]}
                                    onChange={(e: any) =>
                                        setFieldValue("searchBy.value", e.target.value)
                                    }
                                    type={"radio"}
                                    selectedValues={["" + values.searchBy.value]}
                                    labelName={getLabelName("Type")}
                                />
                            </div>

                            <div style={{ width: 170 }}>
                                <TextBox
                                    inputName="searchBy.word"
                                    inputValue={values.searchBy.word}
                                    labelName=""
                                    onChange={(e: any) =>
                                        setFieldValue("searchBy.word", e.target.value)
                                    }
                                    placeHolder={getLabelName("Search")}
                                />
                            </div>

                            <ButtonBox type="submit">{getLabelName("Search")}</ButtonBox>
                        </div>
                    </div>
                </div>
            </form>

            {/* Modal */}
            <ModelDialogBox
                isXCloseButtonVisible={true}
                isModelVisible={showSelectBeneficiary}
                isCloseButtonVisible={false}
                isEscapeCloseEnabled={true}
                title={getLabelName("Customer")}
                size="lg"
                onCloseEvent={() => setShowSelectBeneficiary(false)}
            >
                <BeneficiaryButtonList
                    type={BeneficiaryTypeEnum.Customer}
                    onActionEvent={async (o: RequestActionModel) => {
                        setShowSelectBeneficiary(false);
                        setFieldValue("customerId.value", o.request.value);
                        setFieldValue("customerId.label", o.request.name);
                    }}
                />
            </ModelDialogBox>
        </LoadingBox>
    );
    //#endregion
};

export default TransactionOrdersForm;
